import { forwardRef, useImperativeHandle, useState } from 'react';
import {
  CalendarDays,
  CheckSquare,
  Code2,
  FileText,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  Image as ImageIcon,
  Layers,
  List,
  ListOrdered,
  Minus,
  Quote,
  StickyNote,
  Table as TableIcon,
  Text,
  Video as VideoIcon,
} from 'lucide-react';
import { Extension, ReactRenderer, type Editor, type Range } from '@tiptap/react';
import { Suggestion, exitSuggestion, type SuggestionOptions } from '@tiptap/suggestion';

import { cn } from '@/lib/utils';
import type { PageProvider } from './pageEmbed';

// ---------------------------------------------------------------------------
// Shared floating popup (no tippy dependency — positioned at the caret rect)
// ---------------------------------------------------------------------------
interface ListItem {
  title: string;
  subtitle?: string;
  icon?: typeof Text;
  /** Carry-through payload for mention (id/label) etc. */
  [key: string]: unknown;
}

interface ListHandle {
  onKeyDown: (event: KeyboardEvent) => boolean;
}

const SuggestionList = forwardRef<ListHandle, { items: ListItem[]; command: (item: ListItem) => void }>(
  function SuggestionList({ items, command }, ref) {
    const [index, setIndex] = useState(0);
    const active = items.length ? Math.min(index, items.length - 1) : 0;

    useImperativeHandle(ref, () => ({
      onKeyDown: (event) => {
        if (!items.length) return false;
        if (event.key === 'ArrowUp') {
          setIndex((i) => (Math.min(i, items.length - 1) + items.length - 1) % items.length);
          return true;
        }
        if (event.key === 'ArrowDown') {
          setIndex((i) => (Math.min(i, items.length - 1) + 1) % items.length);
          return true;
        }
        if (event.key === 'Enter') {
          command(items[active]);
          return true;
        }
        return false;
      },
    }));

    if (!items.length) {
      return (
        <div className="bg-popover text-popover-foreground rounded-md border p-2 text-sm shadow-md">
          No results
        </div>
      );
    }

    return (
      <div className="bg-popover text-popover-foreground max-h-72 w-64 overflow-y-auto rounded-md border p-1 shadow-md">
        {items.map((item, i) => {
          const Icon = item.icon;
          return (
            <button
              key={item.title + i}
              type="button"
              onClick={() => command(item)}
              className={cn(
                'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm',
                i === active ? 'bg-accent text-accent-foreground' : 'hover:bg-muted',
              )}
            >
              {Icon ? <Icon className="size-4 shrink-0" /> : null}
              <span className="min-w-0">
                <span className="block truncate">{item.title}</span>
                {item.subtitle ? (
                  <span className="text-muted-foreground block truncate text-xs">{item.subtitle}</span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
    );
  },
);

type RenderFactory = SuggestionOptions['render'];

function makeRender(): RenderFactory {
  let renderer: ReactRenderer<ListHandle> | null = null;
  let el: HTMLDivElement | null = null;
  // Dismiss the popup when the user clicks anywhere outside it (e.g. the empty
  // page area below the content, where clicking doesn't move the editor
  // selection so the plugin wouldn't otherwise exit).
  let onOutside: ((e: PointerEvent) => void) | null = null;

  const place = (rect: (() => DOMRect | null) | null | undefined) => {
    const r = rect?.();
    if (!r || !el) return;
    el.style.left = `${r.left}px`;
    el.style.top = `${r.bottom + 6}px`;
  };

  return () => ({
    onStart: (props) => {
      renderer = new ReactRenderer(SuggestionList, { props, editor: props.editor });
      el = document.createElement('div');
      el.style.position = 'fixed';
      el.style.zIndex = '60';
      document.body.appendChild(el);
      el.appendChild(renderer.element);
      place(props.clientRect);
      onOutside = (e) => {
        if (el && !el.contains(e.target as Node)) exitSuggestion(props.editor.view);
      };
      document.addEventListener('pointerdown', onOutside, true);
    },
    onUpdate: (props) => {
      renderer?.updateProps(props);
      place(props.clientRect);
    },
    onKeyDown: (props) => {
      if (props.event.key === 'Escape') return true;
      return renderer?.ref?.onKeyDown(props.event) ?? false;
    },
    onExit: () => {
      if (onOutside) document.removeEventListener('pointerdown', onOutside, true);
      onOutside = null;
      el?.remove();
      renderer?.destroy();
      el = null;
      renderer = null;
    },
  });
}

// ---------------------------------------------------------------------------
// Slash command (/) — insert blocks
// ---------------------------------------------------------------------------
interface SlashItem extends ListItem {
  run: (editor: Editor, range: Range) => void;
}

const SLASH_ITEMS: SlashItem[] = [
  { title: 'Text', icon: Text, run: (e, r) => e.chain().focus().deleteRange(r).setParagraph().run() },
  { title: 'Heading 1', icon: Heading1, run: (e, r) => e.chain().focus().deleteRange(r).toggleHeading({ level: 1 }).run() },
  { title: 'Heading 2', icon: Heading2, run: (e, r) => e.chain().focus().deleteRange(r).toggleHeading({ level: 2 }).run() },
  { title: 'Heading 3', icon: Heading3, run: (e, r) => e.chain().focus().deleteRange(r).toggleHeading({ level: 3 }).run() },
  { title: 'Heading 4', icon: Heading4, run: (e, r) => e.chain().focus().deleteRange(r).toggleHeading({ level: 4 }).run() },
  { title: 'Heading 5', icon: Heading5, run: (e, r) => e.chain().focus().deleteRange(r).toggleHeading({ level: 5 }).run() },
  { title: 'Heading 6', icon: Heading6, run: (e, r) => e.chain().focus().deleteRange(r).toggleHeading({ level: 6 }).run() },
  { title: 'Bullet list', icon: List, run: (e, r) => e.chain().focus().deleteRange(r).toggleBulletList().run() },
  { title: 'Numbered list', icon: ListOrdered, run: (e, r) => e.chain().focus().deleteRange(r).toggleOrderedList().run() },
  { title: 'To-do list', icon: CheckSquare, run: (e, r) => e.chain().focus().deleteRange(r).toggleTaskList().run() },
  { title: 'Quote', icon: Quote, run: (e, r) => e.chain().focus().deleteRange(r).toggleBlockquote().run() },
  { title: 'Callout', subtitle: 'Highlighted note', icon: StickyNote, run: (e, r) => e.chain().focus().deleteRange(r).toggleCallout().run() },
  { title: 'Code block', icon: Code2, run: (e, r) => e.chain().focus().deleteRange(r).toggleCodeBlock().run() },
  { title: 'Table', icon: TableIcon, run: (e, r) => e.chain().focus().deleteRange(r).insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
  { title: 'Divider', icon: Minus, run: (e, r) => e.chain().focus().deleteRange(r).setHorizontalRule().run() },
  {
    title: 'Date',
    subtitle: 'Insert today’s date',
    icon: CalendarDays,
    run: (e, r) => {
      const today = new Date().toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      e.chain().focus().deleteRange(r).insertContent(`${today} `).run();
    },
  },
  {
    title: 'Video',
    subtitle: 'YouTube, Vimeo or URL',
    icon: VideoIcon,
    run: (e, r) => {
      const url = window.prompt('Video URL (YouTube, Vimeo, or embed link)');
      e.chain().focus().deleteRange(r).run();
      if (url) e.chain().focus().setVideo({ src: url }).run();
    },
  },
];

/** Open a native file picker for a single image; resolves null if cancelled. */
export function pickImageFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.click();
  });
}

/** The "/image" item — uploads a picked file via `uploadImage` when provided,
 *  else falls back to prompting for a URL. */
function imageSlashItem(uploadImage?: (file: File) => Promise<string>): SlashItem {
  return {
    title: 'Image',
    icon: ImageIcon,
    run: (e, r) => {
      e.chain().focus().deleteRange(r).run();
      if (uploadImage) {
        void (async () => {
          const file = await pickImageFile();
          if (!file) return;
          const url = await uploadImage(file);
          if (url) e.chain().focus().setImage({ src: url }).run();
        })();
        return;
      }
      const url = window.prompt('Image URL');
      if (url) e.chain().focus().setImage({ src: url }).run();
    },
  };
}

// Page-embed items are only offered when the host supplies a page provider
// (standalone Wiki / project Pages) — FE-ADR-055.
const PAGE_ITEMS: SlashItem[] = [
  {
    title: 'Page',
    subtitle: 'Link to another page',
    icon: FileText,
    run: (e, r) => e.chain().focus().deleteRange(r).setPageEmbed({ mode: 'link' }).run(),
  },
  {
    title: 'Page content',
    subtitle: "Embed a page's content inline",
    icon: FileText,
    run: (e, r) => e.chain().focus().deleteRange(r).setPageEmbed({ mode: 'content' }).run(),
  },
  {
    title: 'Child pages',
    subtitle: 'List a page’s sub-pages',
    icon: Layers,
    run: (e, r) => e.chain().focus().deleteRange(r).setPageEmbed({ mode: 'children' }).run(),
  },
];

export function slashCommand(opts: { pageProvider?: PageProvider } = {}) {
  const items = [
    ...SLASH_ITEMS,
    imageSlashItem(),
    ...(opts.pageProvider ? PAGE_ITEMS : []),
  ];
  return Extension.create({
    name: 'slashCommand',
    addProseMirrorPlugins() {
      return [
        Suggestion<SlashItem>({
          editor: this.editor,
          char: '/',
          startOfLine: false,
          items: ({ query }) =>
            items.filter((i) => i.title.toLowerCase().includes(query.toLowerCase())).slice(0, 20),
          command: ({ editor, range, props }) => props.run(editor, range),
          render: makeRender(),
        }),
      ];
    },
  });
}

// ---------------------------------------------------------------------------
// @mention — project members
// ---------------------------------------------------------------------------
export interface MentionItem {
  id: number;
  label: string;
}

export function mentionSuggestion(members: MentionItem[]): Partial<SuggestionOptions> {
  return {
    char: '@',
    items: ({ query }) =>
      members
        .filter((m) => m.label.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 8)
        .map((m) => ({ ...m, title: m.label })),
    command: ({ editor, range, props }) => {
      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          { type: 'mention', attrs: { id: (props as MentionItem).id, label: (props as MentionItem).label } },
          { type: 'text', text: ' ' },
        ])
        .run();
    },
    render: makeRender(),
  };
}
